// VAERION Platform — build 2026-03-30T0645
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from './App';
import './index.css';
import { retireLegacyServiceWorkers } from './lib/retireLegacyServiceWorkers';

// Initialize Sentry error tracking
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
// PHI-safe URL scrubber — removes tokens, auth params, and any path segments
// that look like identifiers from URLs before they are sent to Sentry.
// Required for HIPAA §164.312(b): protect PHI in audit/logging systems.
function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const sensitiveParams = ['token', 'key', 'magic', 'code', 'access_token', 'id_token', 'session'];
    sensitiveParams.forEach((p) => u.searchParams.delete(p));
    return u.toString();
  } catch {
    return '[invalid-url]';
  }
}

// PHI field names — never send these values to a third-party error tracker.
const PHI_FIELDS = new Set([
  'email', 'phone', 'ssn', 'dob', 'date_of_birth', 'first_name', 'last_name',
  'name', 'address', 'zip', 'password', 'Authorization', 'Cookie', 'cookie',
  'set-cookie', 'x-user-email',
]);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
    // Only send user ID — never email, name, or any PHI identifier.
    // HIPAA §164.514: de-identification requires removing 18 identifiers.
    beforeSend(event) {
      // 1. Strip PHI from user context (keep only opaque ID)
      if (event.user) {
        event.user = { id: event.user.id };
      }

      // 2. Strip sensitive request headers and body
      if (event.request) {
        if (event.request.headers) {
          const safeHeaders: Record<string, string> = {};
          for (const [k, v] of Object.entries(event.request.headers)) {
            if (!PHI_FIELDS.has(k.toLowerCase())) safeHeaders[k] = v;
          }
          event.request.headers = safeHeaders;
        }
        // Never send request body — may contain PHI
        delete event.request.data;
        delete event.request.cookies;
        if (event.request.url) event.request.url = scrubUrl(event.request.url);
      }

      // 3. Scrub breadcrumb URLs and strip PHI from breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => {
          if (b.data?.url) b.data.url = scrubUrl(b.data.url);
          if (b.data) {
            for (const key of Object.keys(b.data)) {
              if (PHI_FIELDS.has(key.toLowerCase())) delete b.data[key];
            }
          }
          return b;
        });
      }

      // 4. Scrub extra/contexts that might contain PHI
      if (event.extra) {
        for (const key of Object.keys(event.extra)) {
          if (PHI_FIELDS.has(key.toLowerCase())) delete event.extra[key];
        }
      }

      return event;
    },
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

void retireLegacyServiceWorkers();

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex flex-col items-center justify-center min-h-screen p-8">
          <h2 className="text-lg font-semibold text-heading mb-2">Something went wrong</h2>
          <p className="text-sm text-secondary mb-4">An unexpected error occurred. Our team has been notified.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-md text-sm hover:bg-primary-hover transition-colors"
          >
            Reload Page
          </button>
        </div>
      }
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
